import React from "react";
import { IServiceFactoryExtended } from '../service-factory-extended.interface';
import { IProgressiveWebAppService } from "./progressive-web-app.service.interface";
import { Check } from '@sparkware/app-utils';
import { observable, action, computed } from "mobx";
import { ServiceBase } from '../ServiceBase';
import { DomEventSubscription } from '../../../utils/event-listener-registration';
import {
    PWA_INSTALLED_CALLBACK, PWA_SHOULD_AUTO_POPUP_LOCAL_STORAGE_KEY,
    PWA_PROMPT_CALLBACK,
    PWA_PROMPT_EVENT,
    PWA_IOS_SHOWN_INSTALL_OFFER_COUNT
} from "../../../common/const/service-worker-constants";
import { stickToBottomTooltipModifier } from "../tooltip/tooltip.modifiers";
import { IosAddToHomeScreenComponent } from "../../components/tooltip/tooltips/ios-add-to-home/ios-add-to-home.component";

const MAX_IOS_ADD_TO_HOMESCREEN_OFFER_SHOWN_TIMES = 2;

export class ProgressiveWebAppService extends ServiceBase implements IProgressiveWebAppService {

    @observable.ref
    private _onBeforeInstallEvent = null;

    private _clickSubscription: DomEventSubscription;

    @computed
    public get isPWAInstallable(): boolean {
        return Check.isFunction(this._onBeforeInstallEvent?.prompt);
    }

    private _isPwaOnIOSShownThisSession: boolean = false;
    private _gameWasOpen: boolean = false;


    constructor(services: IServiceFactoryExtended) {
        super(services);

        const pwaEvent = this.services.window.getGlobalProperty(PWA_PROMPT_EVENT);
        if (pwaEvent) {
            // this means beforeinstallprompt handler was already called
            this._init(pwaEvent);
        }

        // beforeinstallprompt can arrive after service constructor finishes
        // and also can be called multiple times

        // register beforeinstallprompt callback
        this.services.window.setGlobalProperty(PWA_PROMPT_CALLBACK, (pwaEvent) => {
            this._init(pwaEvent);
        });

        // register appinstalled callback
        this.services.window.setGlobalProperty(PWA_INSTALLED_CALLBACK, () => {
            this._onAppInstalled();
        });

        this._initReactions();
    }

    // can be called multiple times by beforeinstallprompt handler
    @action.bound
    private _init(onBeforeInstallEvent: any) {
        // disable it for hybrid and mini Orbit
        if (this.services.miniOrbit.isEnabled || this.services.device.hybrid.isHybrid) {
            return;
        }

        this._onBeforeInstallEvent = onBeforeInstallEvent;
    }

    private _subscribeToMainScreenClick() {
        this._unsubscribeToMainScreenClick();
        this._clickSubscription = this.services.mainScreen.subscribeTo.click(() => {
            this._tryToShowPwaInstall();
        });
    }

    private _unsubscribeToMainScreenClick() {
        if (this._clickSubscription) {
            this._clickSubscription.dispose();
            this._clickSubscription = null;
        }
    }

    @action.bound
    private _onAppInstalled() {
        this.services.analytics.sendAnalytics("PWA", "Browser_Prompt", "INSTALL");
        this.services.logger.debug('PWA installed');
        this.services.window.setGlobalProperty(PWA_PROMPT_CALLBACK, null);
        this._onBeforeInstallEvent = null;
    }

    private _initReactions() {

        this.reaction(() => {
            return {
                isAuthorized: this.services.user.isAuthorized,
                isCstatus: this.services.cmsSegmentation.isCstatus,
                isUKOrIrl: this.services.cmsSegmentation.isUKOrIrl
            }
        }, data => {
            if (data.isAuthorized) {
                const shouldAutoPopup = data.isCstatus && !data.isUKOrIrl;
                this.services.storage.setItem(PWA_SHOULD_AUTO_POPUP_LOCAL_STORAGE_KEY, shouldAutoPopup);
            }
        }, { fireImmediately: true }
        );

        this.reaction(() => this.services.mainScreen.mainScreenElement,
            (mainScreen) => {
                if (mainScreen) {
                    this._subscribeToMainScreenClick();
                    this._initIOSPwa();

                    // add to analytics PWA launch event
                    const segments = this.services.device.segments;
                    if (segments?.isProgressiveWebApp) {
                        const CID = this.services.user.cid;
                        this.services.analytics.sendAnalytics("PWA", "Launch", CID);
                    }
                }
            }, { fireImmediately: true }
        );

    }

    private _isIOSPwaEligible() {
        const extraProops = this.services.configuration.startupConfiguration.orbit.extraProperties;
        if (!extraProops.pwaEnabled)
            return false;
        if (this.services.device.hybrid.isHybrid)
            return false;
        if (this.services.miniOrbit.isEnabled)
            return false;
        if (this.services.device.isStandalone)
            return false;
        return true;
    }

    private _initIOSPwa() {
        if (this.services.device.isStandalone) {
            //launched webApp from standalone app (home screen link) - no futher logic needed to prompt for installation
            this.services.analytics.sendAnalytics("PWA", "Launch", this.services.user.cid);
            return;
        }

        //relevant only for IPhone for now
        if (!this._isIOSPwaEligible() || !this.services.device.model.isIphone) {
            this.services.logger.debug(`IOS Add to Home Screen - not eligible`);
            return;
        }

        //we are eligible to show install tooltip from this point on
        this.services.sdk.events.onGameOpen(() => {
            if (this.services.user.isAuthorized)
                this._gameWasOpen = true;
        });

        //since this.services.game.isGameRunning is not an observable, we need to trigger _handleIOSAuthorizedLogic manally on CGP event
        this.services.sdk.events.onGameClose(() => {
            if (this.services.user.isAuthorized)
                this._showIOSTooltip();
        });

        this.reaction(() => {
            return {
                isAuthorized: this.services.user.isAuthorized,
                hasOpenDialogs: this.services.dialog.currentDialogs.length > 0
            }},
            (data) => {
                if (!data.isAuthorized) {
                    //reset all IOS related flags
                    //and also close popup if this is the case
                    if (this._isPwaOnIOSShownThisSession) {
                        this.services.tooltip.closeCurrentTooltip();
                    }
                    this._isPwaOnIOSShownThisSession = false;
                    this._gameWasOpen = false;
                    return;
                }

                //authorized state
                this._showIOSTooltip();
            },
            { fireImmediately: true }
        );
    }

    private _showIOSTooltip() {
        //prevent showing more than once per session
        if (this._isPwaOnIOSShownThisSession) {
            this.services.logger.debug(`Cannot show IOS Add to Home Screen - already shown this session`);
            return;
        }

        //or don't show if a game was not open
        if(!this._gameWasOpen) {
            this.services.logger.debug(`Cannot show IOS Add to Home Screen - no game opened yet`);
            return;
        }

        if (!this.services.user.isAuthorized) {
            this.services.logger.debug(`Cannot show IOS Add to Home Screen - not authorized`);
            return;
        }

        if (this.services.runningGame.isGameRunning) {
            this.services.logger.debug(`Cannot show IOS Add to Home Screen - game is running`);
            return;
        }

        if (this.services.dialog.currentDialogs.length > 0) {
            this.services.logger.debug(`Cannot show IOS Add to Home Screen - popups are shown`);
            return;
        }

        //or if maximum shown number was reached
        const shownTimes = Number(this.services.storage.getItem(PWA_IOS_SHOWN_INSTALL_OFFER_COUNT));
        if (shownTimes >= MAX_IOS_ADD_TO_HOMESCREEN_OFFER_SHOWN_TIMES) {
            this.services.logger.debug(`IOS Add to Home Screen - presented count exceeds ${MAX_IOS_ADD_TO_HOMESCREEN_OFFER_SHOWN_TIMES} times!`);
            return;
        }

        this.services.tooltip.createTooltip(
            <IosAddToHomeScreenComponent
                brandName={this.services.sdk.getSkin().getBrandName()}
                onClicked={() => { this.services.tooltip.closeCurrentTooltip(); }}
            />, {
                styling: {
                    textColor: "black",
                    backgroundColor: "white"
                },
                showCloseButton: true,
                invertCloseButtonColors: true,
                closeOnClickOutside: true,
                showOverlay: false,
                onTooltipClosed: () => {
                    this.services.analytics.sendAnalytics("PWA", "Banner_Prompt", "Closed");
                }
            },
            [stickToBottomTooltipModifier]
        );

        this.services.analytics.sendAnalytics("PWA", "Banner_Prompt", "SHOW");

        this._isPwaOnIOSShownThisSession = true;
        this.services.storage.setItem(PWA_IOS_SHOWN_INSTALL_OFFER_COUNT, shownTimes + 1);
    }

    private _shouldAutoPrompt(): boolean {
        const shouldPrompt = this.services.storage.getItem(PWA_SHOULD_AUTO_POPUP_LOCAL_STORAGE_KEY) === 'true' && !this.services.cmsSegmentation.isAppBannerUKorIRL;
        return this.isPWAInstallable
            && shouldPrompt
            && !this.services.user.isAuthorized;
    }

    private _tryToShowPwaInstall() {
        if (this._shouldAutoPrompt()) {
            this.prompt().catch(err => {
                this.services.logger.error('Attempt to show PWA prompt failed', err);
            });
        }
    }

    prompt() {
        if (!this.isPWAInstallable) {
            return Promise.reject("Not a PWA");
        }

        try {
            return this._onBeforeInstallEvent.prompt()
                .then(res => {
                    this._unsubscribeToMainScreenClick();
                    this.services.analytics.sendAnalytics("PWA", "Browser_Prompt", "SHOW");
                    this.services.logger.debug(`PWA prompt() success with outcome: ${res && res.outcome}`);
                    return res;
                });
        }
        catch (err) {
            this.services.logger.error('Pwa exception caught', err);
            throw err;
        }
    }
}
